import {
  IconArrowBarToDown,
  IconCurrencyDollar,
  IconDeviceGamepad2,
  IconFlame,
  IconHome,
  IconTicket,
  IconTrophy,
} from "@tabler/icons-react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import animalitosIcon from "@/assets/images/floating-menu-icons/lottery-animalitos-float.svg";
import terminalesIcon from "@/assets/images/floating-menu-icons/lottery-terminal-float.svg";
import triplesIcon from "@/assets/images/floating-menu-icons/lottery-triples-float.svg";
import {useAuthStore} from "@/store/useAuth.store";
import {useBalanceStore} from "@/store/useBalance.store";
import {SLOTS_GAMES_NAVIGATION} from "@/util/categoryDisplayName";
import styles from "./MobileBottomDock.module.css";

/**
 * @typedef {"slots" | "lottery" | "feed" | "sports" | "deposit"} DockItemId
 */

/** Route prefix matching for active detection */
const ROUTE_MAP = {
  slots: ["/casino", "/games", "/gameslist", "/mini-juegos"],
  lottery: ["/lottery", "/lotoven"],
  feed: ["/game-feed"],
  sports: ["/sports"],
};

const HOME_REPLACEABLE_IDS = new Set(["slots", "lottery", "sports", "feed"]);

const ICON_SIZE = 22;
const CENTER_ICON_SIZE = 26;

const MobileBottomDock = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useTranslation();
  const [isLotteryMenuOpen, setIsLotteryMenuOpen] = useState(false);

  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);

  const activeId = useMemo(() => {
    const path = location.pathname;
    for (const [id, prefixes] of Object.entries(ROUTE_MAP)) {
      if (prefixes.some(p => path === p || path.startsWith(`${p}/`))) {
        return id;
      }
    }
    return null;
  }, [location.pathname]);

  useEffect(() => {
    setIsLotteryMenuOpen(false);
  }, [location.pathname]);

  const requireAuth = useCallback(
    callback => {
      if (authStatus !== "authenticated") {
        useAuthStore.getState().handleAuthForm("login");
        handleShowAuthModal();
        return;
      }
      callback();
    },
    [authStatus, handleShowAuthModal]
  );

  const navigateTo = useCallback(
    (path, options) => {
      navigate(path, options);
      window.scrollTo({top: 0, behavior: "smooth"});
    },
    [navigate]
  );

  const handleHome = useCallback(() => {
    setIsLotteryMenuOpen(false);
    navigateTo("/");
  }, [navigateTo]);

  const handleSlots = useCallback(() => {
    setIsLotteryMenuOpen(false);
    requireAuth(() => navigateTo(SLOTS_GAMES_NAVIGATION.pathname));
  }, [requireAuth, navigateTo]);

  const handleLottery = useCallback(() => {
    requireAuth(() => setIsLotteryMenuOpen(open => !open));
  }, [requireAuth]);

  const handleFeed = useCallback(() => {
    setIsLotteryMenuOpen(false);
    requireAuth(() => navigateTo("/game-feed"));
  }, [requireAuth, navigateTo]);

  const handleSports = useCallback(() => {
    setIsLotteryMenuOpen(false);
    requireAuth(() => navigateTo("/sports"));
  }, [requireAuth, navigateTo]);

  const handleDeposit = useCallback(() => {
    setIsLotteryMenuOpen(false);
    requireAuth(() => handleBankAccountModal());
  }, [requireAuth, handleBankAccountModal]);

  const lotteryMenuItems = useMemo(
    () => [
      {
        key: "animalitos",
        label: t("Animalitos", "Animalitos"),
        path: "/lottery/animalitos",
        iconSrc: animalitosIcon,
      },
      {
        key: "triples",
        label: t("Triples", "Triples"),
        path: "/lottery/triples",
        iconSrc: triplesIcon,
      },
      {
        key: "terminales",
        label: t("Terminales", "Terminales"),
        path: "/lottery/terminales",
        iconSrc: terminalesIcon,
      },
      {
        key: "tripleta",
        label: t("tripleta", "Tripleta"),
        path: "/lottery/tripleta",
        Icon: IconCurrencyDollar,
        iconClassName: styles.lotteryMenuItemIconIndigo,
      },
    ],
    [t]
  );

  const handleLotteryItemClick = useCallback(
    path => {
      setIsLotteryMenuOpen(false);
      navigateTo(path);
    },
    [navigateTo]
  );

  const sideItems = useMemo(
    () => [
      {
        id: "slots",
        label: t("floatingMenuSlots"),
        icon: IconDeviceGamepad2,
        onClick: handleSlots,
      },
      {
        id: "lottery",
        label: t("floatingMenuLottery"),
        icon: IconTicket,
        onClick: handleLottery,
      },
      {
        id: "sports",
        label: t("floatingMenuSports"),
        icon: IconTrophy,
        onClick: handleSports,
      },
      {
        id: "feed",
        label: t("floatingMenuFeed"),
        icon: IconFlame,
        onClick: handleFeed,
      },
    ],
    [t, handleSlots, handleLottery, handleSports, handleFeed]
  );

  const resolveDockItem = useCallback(
    item => {
      if (activeId === item.id && HOME_REPLACEABLE_IDS.has(item.id)) {
        return {
          ...item,
          displayId: `${item.id}-home`,
          label: t("floatingMenuHome"),
          icon: IconHome,
          onClick: handleHome,
        };
      }
      return {...item, displayId: item.id};
    },
    [activeId, t, handleHome]
  );

  const renderSideItem = useCallback(
    (item, extraProps = {}) => {
      const resolved = resolveDockItem(item);
      const Icon = resolved.icon;
      const isLotteryToggle = item.id === "lottery" && resolved.displayId === "lottery";

      return (
        <button
          key={resolved.displayId}
          type="button"
          className={`${styles.dockItem}${isLotteryToggle && isLotteryMenuOpen ? ` ${styles.dockItemActive}` : ""}`}
          onClick={resolved.onClick}
          aria-label={resolved.label}
          aria-expanded={isLotteryToggle ? isLotteryMenuOpen : undefined}
          aria-haspopup={isLotteryToggle ? "menu" : undefined}
          {...extraProps}
        >
          <span className={styles.dockIndicator} />
          <span className={styles.dockIcon}>
            <Icon
              size={ICON_SIZE}
              stroke={1.8}
            />
          </span>
          <span className={styles.dockLabel}>{resolved.label}</span>
        </button>
      );
    },
    [resolveDockItem, isLotteryMenuOpen]
  );

  return (
    <>
      {isLotteryMenuOpen && (
        <div
          className={styles.lotteryMenuOverlay}
          onClick={() => setIsLotteryMenuOpen(false)}
          aria-hidden={!isLotteryMenuOpen}
        >
          <div
            className={styles.lotteryMenu}
            role="menu"
            aria-label={t("floatingMenuLottery")}
            onClick={e => e.stopPropagation()}
          >
            <div className={styles.lotteryMenuList}>
              {lotteryMenuItems.map(item => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className={styles.lotteryMenuItem}
                  onClick={() => handleLotteryItemClick(item.path)}
                >
                  <span
                    className={`${styles.lotteryMenuItemIcon}${item.iconClassName ? ` ${item.iconClassName}` : ""}`}
                  >
                    {item.iconSrc ? (
                      <img
                        src={item.iconSrc}
                        alt=""
                        aria-hidden
                      />
                    ) : (
                      <item.Icon
                        size={20}
                        stroke={1.8}
                      />
                    )}
                  </span>
                  <span className={styles.lotteryMenuItemLabel}>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <nav
        className={`${styles.dock} bottomNavMenu`}
        aria-label="Main navigation"
      >
        <div className={styles.dockRow}>
          {/* Left two items */}
          {sideItems.slice(0, 2).map(item => renderSideItem(item))}

          {/* Center — Deposit (prominent) */}
          <button
            type="button"
            className={styles.dockCenter}
            onClick={handleDeposit}
            aria-label={t("floatBtnDeposit")}
          >
            <span className={styles.centerRing} />
            <span className={styles.centerPulse} />
            <span className={styles.centerBtn}>
              <IconArrowBarToDown
                size={CENTER_ICON_SIZE}
                stroke={2}
              />
            </span>
            <span className={styles.centerLabel}>{t("floatBtnDeposit")}</span>
          </button>

          {/* Right two items */}
          {sideItems.slice(2).map(item => renderSideItem(item))}
        </div>
      </nav>
    </>
  );
};

export default MobileBottomDock;
